<template>
  <div class="customer-management">
    <!-- 查询区域 -->
    <!-- 优化后的查询区域 -->
    <a-card title="客户查询" :bordered="false" class="search-card">
      <div class="search-container">
        <a-form layout="inline" :model="queryState">
          <div class="search-content">
            <!-- 动态显示的表单项 -->
            <div class="search-fields">
              <template>
                <a-form-item
                    v-for="field in filterConfig" :key="field.field"
                    :label="field.label"
                    class="compact-form-item"
                >
                  <component
                      :is="getFieldComponent(field.field)"
                      v-model="queryState[field.field]"
                      :placeholder="field.placeholder || undefined"
                      allow-clear
                      class="search-input"
                      v-bind="getFieldProps(field.field)"
                  />
                </a-form-item>
              </template>
            </div>

            <!-- 固定在右侧的查询按钮 -->
            <div class="search-actions">
              <a-space>
                <a-button type="primary" @click="handleSearch">查询</a-button>
                <a-button @click="handleReset">重置</a-button>
              </a-space>
            </div>
          </div>
        </a-form>
      </div>
    </a-card>


    <!-- 表格区域 -->
    <a-card title="客户列表" :bordered="false" class="table-card">
      <a-table
          :columns="columns"
          :data-source="tableData"
          :pagination="pagination"
          :loading="loading"
          row-key="id"
          bordered
          size="middle"
          @change="handleTableChange"
      >
        <span slot="action" slot-scope="text, record">
            <a-button type="link" @click="handleViewDetail(record)">查看</a-button>
        </span>
      </a-table>
    </a-card>

    <!-- 详情对话框 -->
    <a-modal
        v-model="detailModalVisible"
        title="客户详细信息"
        width="800px"
        centered
        @cancel="handleCloseDetailModal"
    >
      <a-descriptions bordered :column="1" size="middle">
        <a-descriptions-item label="系统ID">{{ currentCustomer.tmsId || '-' }}</a-descriptions-item>
        <a-descriptions-item label="用户姓名">{{ currentCustomer.userName || '-' }}</a-descriptions-item>
        <a-descriptions-item label="英文名">{{ currentCustomer.userEnName || '-' }}</a-descriptions-item>
        <a-descriptions-item label="手机号">{{ currentCustomer.mobile || '-' }}</a-descriptions-item>
        <!-- 身份证号 -->
        <a-descriptions-item label="护照号">{{ currentCustomer.idCard || '-' }}</a-descriptions-item>
        <a-descriptions-item label="PINI CODE">{{ currentCustomer.taxId || '-' }}</a-descriptions-item>
        <a-descriptions-item label="身份证照片">
          <div style="display: flex; gap: 16px;">
            <template v-if="currentCustomer.pic1">
              <div style="display: flex; flex-direction: column; align-items: flex-start;">
                <img
                    :src="currentCustomer.pic1"
                    style="width: 120px; border: 1px solid #f0f0f0;"
                    @error="handleImageError"
                />
                <a-button type="link" @click="previewImage(currentCustomer.pic1)">查看大图</a-button>
              </div>
            </template>
            <template v-if="currentCustomer.pic2">
              <div style="display: flex; flex-direction: column; align-items: flex-start;">
                <img
                    :src="currentCustomer.pic2"
                    style="width: 120px; border: 1px solid #f0f0f0;"
                    @error="handleImageError"
                />
                <a-button type="link" @click="previewImage(currentCustomer.pic2)">查看大图</a-button>
              </div>
            </template>
            <span v-if="!currentCustomer.pic1 && !currentCustomer.pic2">-</span>
          </div>
        </a-descriptions-item>
        <a-descriptions-item label="客户编码">{{ currentCustomer.customerNo || '-' }}</a-descriptions-item>
        <a-descriptions-item label="注册时间">{{ currentCustomer.customerRegisterTime || '-' }}</a-descriptions-item>
        <a-descriptions-item label="首单时间">{{ currentCustomer.fristOrderTime || '-' }}</a-descriptions-item>
        <a-descriptions-item label="末单时间">{{ currentCustomer.lastOrderTime || '-' }}</a-descriptions-item>
        <a-descriptions-item label="总下单数">{{ currentCustomer.sumOrderCount || 0 }}</a-descriptions-item>
        <a-descriptions-item label="账户余额">{{ currentCustomer.balance || 0 }}</a-descriptions-item>
        <a-descriptions-item label="用户积分">{{ currentCustomer.integrate || 0 }}</a-descriptions-item>
        <a-descriptions-item label="总消费">{{ currentCustomer.totalUzConsume || '-' }}</a-descriptions-item>

        <!-- 详细地址（带 OSS 格式） -->
        <a-descriptions-item label="详细地址" :span="2">
          <template v-if="currentCustomer.address">
            <a-tag color="blue">OSS 地址</a-tag>
            <a :href="currentCustomer.address" target="_blank">{{ currentCustomer.address }}</a>
          </template>
          <span v-else>-</span>
        </a-descriptions-item>

        <a-descriptions-item label="备注" :span="2">{{ currentCustomer.remark || '-' }}</a-descriptions-item>
      </a-descriptions>
      <template #footer>
        <a-button type="primary" @click="handleCloseDetailModal">关闭</a-button>
      </template>
    </a-modal>
    <!-- 预览 Modal -->
    <a-modal
        v-model="previewVisible"
        :footer="null"
        width="80%"
        title="图片预览"
        @cancel="previewVisible = false"
    >
      <img :src="previewImageUrl" style="width: 100%; max-height: 80vh; object-fit: contain;"/>
    </a-modal>
  </div>
</template>

<script setup>
import {ref, reactive, onMounted} from 'vue';
import {message} from 'ant-design-vue';
import {getCustomerList, getCustomerDetail} from 'api/customer';
import moment from 'moment';

// 查询状态
const queryState = reactive({
  userName: '',
  mobile: '',
  idCard: '',
  customerNo: '',
  startTime: '',
  endTime: ''
});

const registerTimeRange = ref([]);

// 表格状态
const tableData = ref([]);
const loading = ref(false);
const pagination = reactive({
  current: 1,
  pageSize: 10,
  pageSizeOptions: ['10', '20', '30', '50'],
  showTotal: total => `共 ${total} 条`,
  showQuickJumper: true,
  showSizeChanger: true,
  total: 0
});
// 详情模态框状态
const detailModalVisible = ref(false);
const currentCustomer = ref({});
const previewVisible = ref(false);
const previewImageUrl = ref('');

// 字段配置
// eslint-disable-next-line no-unused-vars
const filterConfig = [
  {label: '用户姓名', field: 'userName', type: 'input', placeholder: '请输入用户姓名'},
  {label: '手机号', field: 'mobile', type: 'input', placeholder: '请输入手机号'},
  {label: '身份证', field: 'idCard', type: 'input', placeholder: '请输入身份证'},
  {label: '客户编码', field: 'customerNo', type: 'input', placeholder: '请输入客户编码'},
  {label: '注册时间', field: 'registerTime', type: 'date', placeholder: undefined}
];
// 根据字段类型返回组件
// eslint-disable-next-line no-unused-vars
const getFieldComponent = (field) => {
  return field === 'registerTime' ? 'a-range-picker' : 'a-input';
};

// 获取字段属性
// eslint-disable-next-line no-unused-vars
const getFieldProps = (field) => {
  if (field === 'registerTime') {
    return {
      style: {width: '320px'},
      showTime: {
        format: 'HH:mm',
        defaultValue: [moment('00:00:00', 'HH:mm:ss'), moment('23:59:59', 'HH:mm:ss')]
      },
      format: "YYYY-MM-DD HH:mm:ss",
      placeholder: ['开始时间', '结束时间'],
      onChange: handleRegisterTimeChange
    };
  }
  return {};
};

// 预览大图
// eslint-disable-next-line no-unused-vars
const previewImage = (url) => {
  previewVisible.value = true;
  previewImageUrl.value = url;
};
// eslint-disable-next-line no-unused-vars
const handleImageError = (event) => {
  // 设置默认错误图片或隐藏图片
  event.target.style.display = 'none';

  // 或者替换为默认错误图片
  // event.target.src = 'path/to/default-error-image.png';

  // 也可以添加错误提示（根据你的UI框架选择合适的方式）
  this.$message.error('图片加载失败');

  // 如果需要控制台记录错误
  console.error('图片加载失败', event);
};
// eslint-disable-next-line no-unused-vars
const handleRegisterTimeChange = (dates, dateStrings) => {
  console.log('dateStrings ===', dates)
  registerTimeRange.value = dates;
  queryState.startTime = dates[0];
  queryState.endTime = dates[1];
};

const loadTableData = async () => {
  loading.value = true;
  try {
    const params = {
      ...queryState,
      startTime: queryState.registerTime ? moment(queryState.registerTime[0]).format('YYYY-MM-DD HH:mm:ss') : '',
      endTime: queryState.registerTime ? moment(queryState.registerTime[1]).format('YYYY-MM-DD HH:mm:ss') : '',
      current: pagination.current,
      size: pagination.pageSize
    };
    const res = await getCustomerList(params);
    tableData.value = res.data.records || [];
    pagination.total = res.data.total || 0;
  } catch (error) {
    console.error('加载客户数据失败:', error);
    message.error('加载客户数据失败');
  } finally {
    loading.value = false;
  }
};

// eslint-disable-next-line no-unused-vars
const handleSearch = () => {
  pagination.current = 1;
  loadTableData();
};

// eslint-disable-next-line no-unused-vars
const handleReset = () => {
  Object.keys(queryState).forEach(key => {
    queryState[key] = '';
  });
  registerTimeRange.value = [];
  pagination.current = 1;
  loadTableData();
};

// eslint-disable-next-line no-unused-vars
const handleTableChange = (pag) => {
  pagination.current = pag.current;
  pagination.pageSize = pag.pageSize;
  loadTableData();
};

// eslint-disable-next-line no-unused-vars
const handleViewDetail = async (record) => {
  try {
    const res = await getCustomerDetail({id: record.id});
    currentCustomer.value = res.data || {};
    detailModalVisible.value = true;
  } catch (error) {
    console.error('获取客户详情失败:', error);
    message.error('获取客户详情失败');
  }
};

// eslint-disable-next-line no-unused-vars
const handleCloseDetailModal = () => {
  detailModalVisible.value = false;
};

// 初始化
onMounted(() => {
  loadTableData();
});

// 表格列配置
// eslint-disable-next-line no-unused-vars
const columns = [
  {
    title: '客户编码',
    dataIndex: 'customerNo',
    key: 'customerNo',
    width: '160px',
    align: 'center'
  },
  {
    title: '用户姓名',
    dataIndex: 'userName',
    key: 'userName',
    ellipsis: true,
    align: 'center'
  },
  {
    title: '手机号',
    dataIndex: 'mobile',
    key: 'mobile',
    align: 'center'
  },
  {
    title: '注册时间',
    dataIndex: 'customerRegisterTime',
    key: 'customerRegisterTime',
    width: '180px',
    align: 'center'
  },
  {
    title: '护照号',
    dataIndex: 'idCard',
    key: 'idCard',
    width: '160px',
    align: 'center'
  },
  {
    title: 'PINI CODE',
    dataIndex: 'taxId',
    key: 'taxId',
    width: '160px',
    align: 'center'
  },
  {
    title: '总下单数',
    dataIndex: 'sumOrderCount',
    key: 'sumOrderCount',
    width: '80px',
    align: 'center'
  },
  {
    title: '账户余额',
    dataIndex: 'balance',
    key: 'balance',
    width: '80px',
    align: 'center'
  }, {
    title: '用户积分',
    dataIndex: 'integrate',
    key: 'integrate',
    width: '80px',
    align: 'center'
  },
  {
    title: '操作',
    key: 'action',
    width: '100px',
    align: 'center',
    fixed: 'right',
    scopedSlots: {customRender: 'action'} // 必须添加这个配置
  }
];
</script>
<style lang="less" scoped>
.customer-management {
  .search-card {
    margin-bottom: 16px;
    border-radius: 4px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);

    :deep(.ant-card-head) {
      min-height: 48px;
      padding: 0 16px;
      border-bottom: none;

      .ant-card-head-title {
        padding: 12px 0;
        font-weight: 500;
      }
    }

    :deep(.ant-card-body) {
      padding: 16px;
    }
  }

  .search-container {
    .search-content {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
      align-items: center;
    }

    .search-fields {
      flex: 1;
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
      align-items: center;
      min-width: 0;
    }

    .search-actions {
      flex-shrink: 0;
      margin-left: auto;
    }

    .compact-form-item {
      margin-bottom: 0;
      flex-shrink: 0;

      :deep(.ant-form-item-label) {
        padding-right: 8px;

        label {
          color: #666;
          font-size: 13px;
        }
      }

      :deep(.ant-form-item-control) {
        min-width: 160px;
      }
    }

    .search-input {
      width: 180px;
    }
  }
}

@media (max-width: 768px) {
  .customer-management {
    .search-container {
      .search-content {
        flex-direction: column;
        align-items: stretch;
        gap: 8px;
      }

      .search-fields {
        width: 100%;
      }

      .search-actions {
        width: 100%;
        margin-left: 0;
        text-align: right;
      }

      .compact-form-item {
        :deep(.ant-form-item-label) {
          display: none;
        }

        :deep(.ant-form-item-control) {
          min-width: 120px;
        }
      }

      .search-input {
        width: 100%;
      }
    }
  }
}
</style>
